import Typography from '@components/v2/ui/Typography/Typography';
import { Stack } from '@mui/material';
import useWidth from '../../../../../../components/Hooks/useWidth';
import LangEnum from '../../../../../../lang/LangEnum';
import { OrderSummaryTestIds } from '../../../../../../utils/testIds/OrderSummary.testIds';
import PriceCompositionUtils from '../../../../../products/prices/utils/priceComposition.utils';
import { useOrderCalculateContext } from '../../../../contexts/OrderCalculate.context';
import { useOrderConditionContext } from '../../../../contexts/OrderCondition.context';
import OrderSummaryTotalQuantity from './OrderSummaryTotalQuantity';
import OrderSummaryTotalValue from './OrderSummaryTotalValue';
import OrderSummaryCompositionTooltip from './components/OrderSummaryCompositionTooltip';
import OrderSummaryIncomeStatus from './components/OrderSummaryIncomeStatus';
import useSummaryLabel, { SUMMARY_COLUMN_GAP_SPACING } from './hooks/useSummaryLabel';
import { sortPriceCompositionForSummary } from './orderSummaryTotals.utils';

interface OrderSummaryTotalsProps {
  isSuggested: boolean;
  isReadOnlyView: boolean;
}

const OrderSummaryTotals = ({ isSuggested, isReadOnlyView }: OrderSummaryTotalsProps) => {
  const { summaryTotals, getConditionTotals } = useOrderCalculateContext();
  const { selectedCondition } = useOrderConditionContext();
  const { mobileWidth } = useWidth();
  const { rowRef, totalsRef, labelRef, label } = useSummaryLabel();

  const totals = selectedCondition
    ? getConditionTotals(selectedCondition.id)
    : summaryTotals?.calculate?.total;
  const hasItemDiscount =
    Number.isFinite(totals?.grossValue) && totals?.grossValue !== totals?.subtotal;
  const priceComposition = sortPriceCompositionForSummary(totals?.priceComposition ?? []);
  const inlineComposition = mobileWidth ? [] : priceComposition;

  return (
    <Stack
      ref={rowRef}
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        columnGap: SUMMARY_COLUMN_GAP_SPACING,
        width: '100%',
        '& .MuiTypography-root': { transform: 'none' },
      }}
    >
      {label && (
        <Typography ref={labelRef} noWrap variant="body2">
          {label}
        </Typography>
      )}

      <Stack
        ref={totalsRef}
        direction="row"
        sx={{
          flexWrap: 'nowrap',
          alignItems: 'center',
          columnGap: 1.5,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <OrderSummaryTotalQuantity
          isStacked={mobileWidth}
          isSuggested={isSuggested}
          totals={totals?.quantity}
          label={totals?.quantity === 1 ? LangEnum.GLOBAL_ITEM_SINGLE : LangEnum.GLOBAL_ITEMS}
          testId={OrderSummaryTestIds.QUANTITY}
        />
        <OrderSummaryTotalQuantity
          isStacked={mobileWidth}
          isSuggested={isSuggested}
          totals={totals?.pieces}
          label={totals?.pieces === 1 ? LangEnum.GLOBAL_PIECE_SINGLE : LangEnum.GLOBAL_PIECES}
          testId={OrderSummaryTestIds.PIECES}
        />

        {hasItemDiscount && (
          <OrderSummaryTotalValue
            isStacked={mobileWidth}
            isStrikethrough={!mobileWidth}
            stackedLabel={LangEnum.GLOBAL_GROSS_VALUE}
            value={totals?.grossValue}
            srLabel={LangEnum.ORDER_SUMMARY_GROSS_ITEMS_LABEL}
            tooltip={LangEnum.ORDER_SUMMARY_GROSS_ITEMS_TOOLTIP}
            testId={OrderSummaryTestIds.GROSS_ITEMS}
          />
        )}

        <OrderSummaryTotalValue
          isStacked={mobileWidth}
          stackedLabel={LangEnum.GLOBAL_NET_VALUE}
          value={totals?.subtotal}
          srLabel={LangEnum.ORDER_SUMMARY_GROSS_LABEL}
          tooltip={LangEnum.ORDER_SUMMARY_SUBTOTAL_TOOLTIP}
          testId={OrderSummaryTestIds.SUBTOTAL}
          labelAdornment={
            <OrderSummaryCompositionTooltip
              isVisible={mobileWidth}
              priceComposition={priceComposition}
            />
          }
        />

        {inlineComposition.map((composition, index) => (
          <OrderSummaryTotalValue
            key={composition.id}
            value={composition.price}
            labelText={PriceCompositionUtils.formatLabel(composition.label)}
            testId={`${OrderSummaryTestIds.PRICE_COMPOSITION}-${index}`}
          />
        ))}

        <OrderSummaryIncomeStatus isReadOnlyView={isReadOnlyView} />
      </Stack>
    </Stack>
  );
};

export default OrderSummaryTotals;
